import { Activity, Clock, CreditCard, FileText, Filter, Mail, MessageSquare, Phone, Save, User, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { cn, formatDateTime } from '@/lib/utils';
import type { CaseNote, CollectionActivity, CollectionCommunication } from '@/types/collections';

// Unified activity type for the consolidated timeline
export type UnifiedActivity = {
  id: string;
  type:
    | 'call'
    | 'email'
    | 'sms'
    | 'note'
    | 'promise'
    | 'payment'
    | 'status_change'
    | 'communication'
    | 'case_note'
    | 'workflow_progress'
    | 'document_upload';
  tag: 'Activity' | 'Note' | 'Communication';
  title: string;
  description: string;
  outcome?: string;
  createdBy: string;
  createdAt: Date;
  // Communication-specific fields
  direction?: 'inbound' | 'outbound';
  subject?: string;
  status?: string;
};

interface ConsolidatedActivityTimelineProps {
  activities: CollectionActivity[];
  communications: CollectionCommunication[];
  notes: CaseNote[];
  isEditMode: boolean;
  onAddNote?: (content: string) => Promise<void>;
}

export function ConsolidatedActivityTimeline({
  activities,
  communications,
  notes,
  isEditMode,
  onAddNote,
}: ConsolidatedActivityTimelineProps) {
  const [newNoteContent, setNewNoteContent] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<string[]>(['Activity', 'Note', 'Communication']);

  // Convert all data to unified format
  const unifiedActivities = useMemo((): UnifiedActivity[] => {
    const unified: UnifiedActivity[] = [];

    // Add regular activities
    activities.forEach((activity) => {
      unified.push({
        id: activity.id,
        type: activity.type,
        tag: 'Activity',
        title: activity.description,
        description: activity.description,
        outcome: activity.outcome,
        createdBy: activity.createdBy,
        createdAt: activity.createdAt,
      });
    });

    // Add communications
    communications.forEach((comm) => {
      unified.push({
        id: comm.id,
        type: comm.type === 'email' ? 'email' : comm.type === 'phone' ? 'call' : 'sms',
        tag: 'Communication',
        title: comm.subject || `${comm.type.charAt(0).toUpperCase() + comm.type.slice(1)} ${comm.direction}`,
        description: comm.content,
        createdBy: comm.sentBy,
        createdAt: comm.sentAt,
        direction: comm.direction,
        subject: comm.subject,
        status: comm.status,
      });
    });

    // Add notes
    notes.forEach((note) => {
      unified.push({
        id: note.id,
        type: 'case_note',
        tag: 'Note',
        title: 'Note Added',
        description: note.content,
        createdBy: note.createdBy,
        createdAt: note.createdAt,
      });
    });

    // Sort by date (most recent first)
    return unified.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }, [activities, communications, notes]);

  // Filter activities based on selected tags
  const filteredActivities = useMemo(() => {
    if (selectedFilters.length === 0) {
      return unifiedActivities;
    }
    return unifiedActivities.filter((activity) => selectedFilters.includes(activity.tag));
  }, [unifiedActivities, selectedFilters]);

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) {
      toast.error('Please enter a note before saving');
      return;
    }

    if (!onAddNote) {
      toast.error('Note functionality not available');
      return;
    }

    setIsSavingNote(true);

    try {
      await onAddNote(newNoteContent.trim());
      setNewNoteContent('');
      toast.success('Note added successfully');
    } catch (error) {
      toast.error('Failed to add note. Please try again.');
      console.error('Error adding note:', error);
    } finally {
      setIsSavingNote(false);
    }
  };

  const getActivityIcon = (type: UnifiedActivity['type']) => {
    switch (type) {
      case 'call':
        return Phone;
      case 'email':
        return Mail;
      case 'sms':
        return MessageSquare;
      case 'case_note':
        return FileText;
      case 'payment':
        return CreditCard;
      case 'workflow_progress':
        return Activity;
      case 'document_upload':
        return FileText;
      default:
        return Activity;
    }
  };

  const getTagColor = (tag: UnifiedActivity['tag']) => {
    switch (tag) {
      case 'Activity':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800';
      case 'Note':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-purple-200 dark:border-purple-800';
      case 'Communication':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400 border-gray-200 dark:border-gray-800';
    }
  };

  const getIconBackgroundColor = (tag: UnifiedActivity['tag']) => {
    switch (tag) {
      case 'Activity':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
      case 'Note':
        return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
      case 'Communication':
        return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';
      default:
        return 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400';
    }
  };

  const clearFilters = () => {
    setSelectedFilters(['Activity', 'Note', 'Communication']);
  };

  const activeFilterCount = 3 - selectedFilters.length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <CardTitle>Activity Timeline</CardTitle>
            <CardDescription>
              {isEditMode
                ? 'Add new notes and view complete history of all activities, notes, and communications'
                : 'Complete history of all activities, notes, and communications'}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {filteredActivities.length} {filteredActivities.length === 1 ? 'item' : 'items'}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filter Controls */}
        <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filter by Type
            </Label>
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 text-xs">
                <X className="h-3 w-3 mr-1" />
                Clear Filters ({activeFilterCount})
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant={selectedFilters.includes('Activity') ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                if (selectedFilters.includes('Activity')) {
                  setSelectedFilters(selectedFilters.filter((f) => f !== 'Activity'));
                } else {
                  setSelectedFilters([...selectedFilters, 'Activity']);
                }
              }}
              className={cn(
                'h-8',
                selectedFilters.includes('Activity') &&
                  'bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-600 dark:hover:bg-blue-700',
              )}
            >
              <Activity className="h-4 w-4 mr-2" />
              Activity
            </Button>
            <Button
              variant={selectedFilters.includes('Note') ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                if (selectedFilters.includes('Note')) {
                  setSelectedFilters(selectedFilters.filter((f) => f !== 'Note'));
                } else {
                  setSelectedFilters([...selectedFilters, 'Note']);
                }
              }}
              className={cn(
                'h-8',
                selectedFilters.includes('Note') &&
                  'bg-purple-600 hover:bg-purple-700 text-white dark:bg-purple-600 dark:hover:bg-purple-700',
              )}
            >
              <FileText className="h-4 w-4 mr-2" />
              Notes
            </Button>
            <Button
              variant={selectedFilters.includes('Communication') ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                if (selectedFilters.includes('Communication')) {
                  setSelectedFilters(selectedFilters.filter((f) => f !== 'Communication'));
                } else {
                  setSelectedFilters([...selectedFilters, 'Communication']);
                }
              }}
              className={cn(
                'h-8',
                selectedFilters.includes('Communication') &&
                  'bg-green-600 hover:bg-green-700 text-white dark:bg-green-600 dark:hover:bg-green-700',
              )}
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Communications
            </Button>
          </div>
        </div>

        {/* Add Note Section - Only visible in Edit Mode */}
        {isEditMode && (
          <>
            <Separator />
            <div className="space-y-3 p-4 border rounded-lg bg-purple-50/50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-900">
              <Label htmlFor="newNote" className="text-sm font-medium flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Add New Note
              </Label>
              <Textarea
                id="newNote"
                value={newNoteContent}
                onChange={(e) => setNewNoteContent(e.target.value)}
                placeholder="Enter your note here..."
                rows={4}
                className="resize-none"
              />
              <div className="flex justify-end">
                <Button onClick={handleAddNote} disabled={isSavingNote || !newNoteContent.trim()} size="sm">
                  {isSavingNote ? (
                    <>
                      <Clock className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Add Note
                    </>
                  )}
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Timeline Display */}
        {filteredActivities.length > 0 ? (
          <>
            <Separator />
            <div className="space-y-4">
              {filteredActivities.map((activity, index) => {
                const Icon = getActivityIcon(activity.type);
                return (
                  <div key={activity.id} className="relative">
                    {index !== filteredActivities.length - 1 && (
                      <div className="absolute left-4 top-10 h-full w-0.5 bg-border" />
                    )}
                    <div className="flex gap-4">
                      <div
                        className={cn(
                          'flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0',
                          getIconBackgroundColor(activity.tag),
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline" className={cn('text-xs font-medium', getTagColor(activity.tag))}>
                                {activity.tag}
                              </Badge>
                              {activity.direction && (
                                <Badge variant="secondary" className="text-xs">
                                  {activity.direction === 'outbound' ? 'Sent' : 'Received'}
                                </Badge>
                              )}
                              {activity.status && (
                                <Badge variant="outline" className="text-xs">
                                  {activity.status}
                                </Badge>
                              )}
                            </div>
                            {activity.subject && <p className="font-medium text-sm">{activity.subject}</p>}
                            <p className="text-sm text-foreground leading-relaxed">{activity.description}</p>
                            {activity.outcome && (
                              <p className="text-sm text-muted-foreground italic">Outcome: {activity.outcome}</p>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDateTime(activity.createdAt.toISOString())}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            <User className="h-3 w-3 mr-1" />
                            {activity.createdBy}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm font-medium">No activities match your filters</p>
            <p className="text-xs mt-1">Try adjusting your filter selection</p>
            {activeFilterCount > 0 && (
              <Button variant="outline" size="sm" onClick={clearFilters} className="mt-4">
                Clear All Filters
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
